/**
 * Read a streamed guide-generation response (/extract, /:id/retry-import) to its end.
 *
 * The routes write one JSON object per line: stage events while working, a heartbeat every fifteen
 * seconds, and finally `done` or `error`. Validation and budget refusals answer before the stream
 * starts, as ordinary JSON with a status code, so a non-OK response is read as that.
 */
export interface ImportDoneEvent {
  stage: 'done';
  guideId?: string | null;
  guide?: { slug?: string; title?: string } | null;
  slugRenamed?: { from: string; to: string };
  saveError?: string;
  [key: string]: unknown;
}

export async function readImportStream(
  res: Response,
  onEvent: (evt: Record<string, any>) => void = () => {},
): Promise<ImportDoneEvent> {
  if (!res.ok || !res.body) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error || 'The import could not start');
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let done: ImportDoneEvent | null = null;
  let streamError = '';
  for (;;) {
    const chunk = await reader.read();
    if (chunk.done) break;
    buffer += decoder.decode(chunk.value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? ''; // keep the trailing partial line for the next chunk
    for (const line of lines) {
      if (!line.trim()) continue;
      const evt = JSON.parse(line);
      onEvent(evt);
      if (evt.stage === 'done') done = evt;
      else if (evt.stage === 'error') streamError = evt.error;
    }
  }
  if (streamError) throw new Error(streamError);
  if (!done) throw new Error('The import ended without a result');
  return done;
}
